import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Player, GameState } from '../types';
import { FaSyncAlt, FaDoorOpen, FaTrophy } from 'react-icons/fa';
import { FaGripLines, FaRotateLeft, FaCheck } from 'react-icons/fa6';
import Cover from '../media/TripleFreezerCover2.jpg';
import {
  completeTournamentPoolMatch,
  completeTournamentPlayoffMatch,
  confirmTournamentMatchSaved,
  subscribeTournament,
} from '../services/tournamentService';
import { enqueueResult, removeResult } from '../services/resultOutbox';
import { buildResultTurnLog } from '../services/turnLog';
import { Tournament } from '../types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: calc(60px + env(safe-area-inset-top, 0px));
  padding-left: ${({ theme }) => theme.spacing(2)};
  padding-right: ${({ theme }) => theme.spacing(2)};
  /* Extra bottom padding to avoid footer overlap and keep cards centered */
  padding-bottom: calc(120px + env(safe-area-inset-bottom, 0px));
  min-height: 100vh;
  width: 100%;
  position: relative;
  background: url(${Cover}) no-repeat center center;
  background-size: cover;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: rgba(0, 13, 26, 1);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 20px;
  z-index: 10;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  text-transform: uppercase;
`;

const MatchHeading = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 1);
  font-family: 'Fjalla One', sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 30px;
  text-align: center;
  text-transform: uppercase;
  text-shadow: 0 0 5px rgba(0, 0, 0, 1);
  background: radial-gradient(4050% 50% at 50% 50%, rgba(255, 255, 255, 0.08), rgba(0, 170, 255, 0) 100%);
  margin-bottom: 20px;
`;

// Tournament summary block (shown when multi-match)
const SummaryCard = styled.div`
  width: 320px;
  border-radius: 10px;
  box-shadow: 0px 5px 5px 0px rgba(0, 0, 0, 0.5);
  background: rgba(0, 32, 64, 0.75);
  padding: 10px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden; /* prevent bleed past rounded corners */
`;

const SummaryHeader = styled.div`
  width: 280px;
  height: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 23px;
  text-align: center;
  text-transform: uppercase;
`;

const SummaryBody = styled.div`
  width: 280px;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 4px 0; /* tighten vertical spacing */
  word-break: break-word;
  overflow-wrap: anywhere; /* avoid overflow for long names */
`;

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SummaryItem = styled.div`
  padding: 6px 0;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  line-height: 19px;

  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 12px;
`;

const ActionButton = styled.button<{ $variant?: 'rematch' | 'exit' }>`
  width: 140px;
  height: 40px;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
  background: linear-gradient(180deg, rgba(0, 127, 255, 1), rgba(0, 32, 64, 1) 100%);
  color: rgba(255, 255, 255, 1);
  border: none;
  cursor: pointer;
  font-family: 'Fjalla One', sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 30px;
  text-align: center;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 127, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PlayerStatsContainer = styled.div<{ $isWinner?: boolean }>`
  width: 320px;
  min-height: 180px;
  box-sizing: border-box;
  border: ${({ $isWinner }) => $isWinner ? '2px solid rgba(255, 213, 0, 1)' : '2px solid rgba(128, 128, 128, 1)'};
  border-radius: 10px;
  box-shadow: ${({ $isWinner }) => $isWinner ? '0px 0px 20px 0px rgba(255, 170, 0, 1)' : '0px 0px 5px 0px rgba(0, 0, 0, 0.5)'};
  background: rgba(0, 32, 64, 0.75);
  margin: 8px 0 12px;
  padding: 10px;
  display: flex;
  flex-direction: column;
`;

const PlayerHeader = styled.div`
  width: 280px;
  height: 40px;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 23px;
  text-align: center;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
`;

const StatsContent = styled.div`
  width: 280px;
  min-height: 120px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  padding: 10px 0;
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const StatsColumn = styled.div`
  width: 140px;
  height: 80px;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: left;
`;

const HistoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const HistoryLabel = styled.span`
  width: 46px;
  height: 19px;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: left;
`;

const HistoryToggle = styled.div<{ $on: boolean }>`
  width: 40px;
  height: 20px;
  border-radius: 10px;
  background: rgba(128, 128, 128, 1);
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const HistoryKnob = styled.div<{ $on: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 1);
  position: absolute;
  left: ${({ $on }) => ($on ? '20px' : '0px')};
  transition: left 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const HistoryContainer = styled.div`
  margin-top: 10px;
  width: 260px;
  height: 80px;
  border-radius: 10px;
  background: rgba(0, 13, 26, 1);
  padding: 10px;
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
`;

const HistoryScroll = styled.div`
  width: 240px;
  height: 60px;
  max-height: 60px;
  overflow-y: auto;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: left;

  /* Custom scrollbar */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(255, 255, 255, 1) transparent; /* Firefox */

  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 1);
    border-radius: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const HistoryItem = styled.div`
  margin-bottom: 4px;
`;

  const DieValue = styled.span<{ $frozen?: boolean }>`
    color: ${({ $frozen }) => ($frozen ? 'rgba(0, 191, 230, 1)' : 'rgba(255, 255, 255, 1)')};
    font-weight: ${({ $frozen }) => ($frozen ? 700 : 400)};
  `;

const ReorderModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: rgba(0, 32, 64, 1);
  border-radius: 10px;
  width: 300px;
  height: 240px;
  box-shadow: 0px 5px 5px 0px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  width: 300px;
  height: 40px;
  background: rgba(0, 13, 26, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 1);
  font-family: 'Fjalla One', sans-serif;
  font-size: 16px;
  text-transform: uppercase;
`;

const ModalBody = styled.div`
  padding: 10px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
`;

const ModalDescription = styled.div`
  width: 260px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: center;
  margin: 0 auto 6px auto;
`;

const PlayerReorderItem = styled.div`
  width: 260px;
  height: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  border-top: 1px solid rgba(128, 128, 128, 1);
  border-bottom: 1px solid rgba(128, 128, 128, 1);
  margin: 0 auto;
  cursor: grab;
`;

const Grip = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 1);
`;

// Legacy button no longer used — removed to satisfy linter

const ModalFooter = styled.div`
  padding: 8px 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  width: ${({ $variant }) => ($variant === 'secondary' ? '110px' : '130px')};
  border-radius: 5px;
  border: none;
  color: rgba(255, 255, 255, 1);
  font-family: 'Fjalla One', sans-serif;
  font-size: 24px;
  text-transform: uppercase;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
  background: ${({ $variant }) =>
    $variant === 'secondary'
      ? 'linear-gradient(180deg, rgba(255, 64, 0, 1), rgba(64, 16, 0, 1) 100%)'
      : 'linear-gradient(180deg, rgba(0, 127, 255, 1), rgba(0, 32, 64, 1) 100%)'};
  
  svg {
    width: 24px;
    height: 24px;
    flex: 0 0 auto;
  }
`;

interface Props {
  winnerName?: string;
  players: Player[];
  clearGameState: () => void;
  startGame: (players: { id: string; name: string }[], matchCount?: number) => void;
  gameState?: GameState;
  isMatchBased?: boolean;
  setPlayers?: (players: Player[]) => void;
}

const WinnerScreen: React.FC<Props> = ({
  winnerName,
  players,
  clearGameState,
  startGame,
  gameState,
  isMatchBased = false,
  setPlayers
}) => {
  const navigate = useNavigate();
  const [playerHistoryStates, setPlayerHistoryStates] = useState<{ [key: string]: boolean }>({});
  const [tournamentSaving, setTournamentSaving] = useState(false);
  const [tournamentSaved, setTournamentSaved] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [savedWithWarning, setSavedWithWarning] = useState('');
  const [tournamentSaveError, setTournamentSaveError] = useState('');
  const [fullTournament, setFullTournament] = useState<Tournament | null>(null);

  // Load tournament data if we're in a tournament match
  const ctx = gameState?.tournamentContext;
  useEffect(() => {
    if (!ctx) return;
    const unsub = subscribeTournament(ctx.tournamentId, setFullTournament);
    return () => unsub();
  }, [ctx]);

  // The tournament match has a fixed player1/player2 order; the LOCAL game order
  // (players[0]/players[1]) can differ — e.g. when the other player was chosen to
  // go first. Win counts must be stored against the MATCH order, or the score
  // shows next to the wrong name on the results/bracket screens.
  const getMatchPlayerUids = (): { p1uid?: string; p2uid?: string } => {
    if (!ctx || !fullTournament) return {};
    const matchObj =
      ctx.phase === 'pool'
        ? fullTournament.poolMatches?.[ctx.matchId]
        : fullTournament.playoffRounds?.[`${ctx.roundIndex}`]?.matches?.[`${ctx.matchIndex}`];
    return { p1uid: matchObj?.player1?.uid, p2uid: matchObj?.player2?.uid };
  };

  const computeTournamentResult = () => {
    if (!ctx) return null;

    const winsByUid: { [uid: string]: number } = {};
    players.forEach((p) => { winsByUid[p.id] = p.matchWins || 0; });
    const { p1uid, p2uid } = getMatchPlayerUids();

    if (isMatchBased && gameState?.matchHistory?.length) {
      const p1 = players[0];
      const p2 = players[1];
      const p1Wins = p1?.matchWins || 0;
      const p2Wins = p2?.matchWins || 0;
      const winnerUid = p1Wins > p2Wins ? p1.id : p2.id;
      const loserWins = Math.min(p1Wins, p2Wins);
      // Win counts keyed to the tournament match's player1/player2 (falls back to
      // local order if the tournament data isn't loaded for some reason).
      const matchP1Wins = p1uid != null ? (winsByUid[p1uid] ?? p1Wins) : p1Wins;
      const matchP2Wins = p2uid != null ? (winsByUid[p2uid] ?? p2Wins) : p2Wins;
      const points: { [uid: string]: number } = {};
      players.forEach((p) => {
        const isWinner = p.id === winnerUid;
        points[p.id] = isWinner ? (loserWins === 0 ? 3 : 2) : (loserWins > 0 ? 1 : 0);
      });

      const stats: { [uid: string]: { triplesAvg: number; accuracy: number; triples: number; freezes: number } } = {};
      const gamesPlayed = p1Wins + p2Wins;
      players.forEach((player) => {
        let totalTriples = 0;
        let totalFreezes = 0;
        let totalHits = 0;
        let totalTurns = 0;
        (gameState.matchHistory || []).forEach((match: any) => {
          const ps = match.playerStats?.find((s: any) => s.playerId === player.id);
          if (ps) {
            totalTriples += ps.triples || 0;
            totalFreezes += ps.frozenDiceCount || 0;
            if (Array.isArray(ps.history)) {
              totalTurns += ps.history.length;
              totalHits += ps.history.filter((h: any) => h.diceValues?.some((v: number) => v > 0)).length;
            }
          }
        });
        stats[player.id] = {
          triplesAvg: gamesPlayed > 0 ? totalTriples / gamesPlayed : 0,
          accuracy: totalTurns > 0 ? (totalHits / totalTurns) * 100 : 0,
          triples: totalTriples,
          freezes: totalFreezes,
        };
      });

      // Keep the throws, not just the aggregates — see services/turnLog.ts.
      const turnLog = buildResultTurnLog(gameState.matchHistory, players);
      return {
        winner: winnerUid,
        player1Wins: matchP1Wins,
        player2Wins: matchP2Wins,
        points,
        stats,
        // RTDB rejects `undefined`, so the key is only present when there is a log.
        ...(turnLog ? { turnLog } : {}),
      };
    } else {
      // Single game (bestOf = 1)
      const winner = players.find((p) => p.hasWon);
      if (!winner) return null;
      const points: { [uid: string]: number } = {};
      players.forEach((p) => { points[p.id] = p.hasWon ? 3 : 0; });
      // Win counts keyed to the tournament match's player1/player2 order
      const player1Wins = p1uid != null ? (winner.id === p1uid ? 1 : 0) : (players[0]?.hasWon ? 1 : 0);
      const player2Wins = p2uid != null ? (winner.id === p2uid ? 1 : 0) : (players[1]?.hasWon ? 1 : 0);
      // Capture skill stats for this one game straight from each player's history
      const stats: { [uid: string]: { triplesAvg: number; accuracy: number; triples: number; freezes: number } } = {};
      players.forEach((player) => {
        const history = player.history || [];
        const triples = history.filter((e: any) => {
          const uniqueVals = Array.from(new Set<number>(e.diceValues as number[]));
          const isTripleTriple = uniqueVals.length === 1 && uniqueVals[0] >= 2 && uniqueVals[0] <= 6;
          const allPositive = e.diceValues.every((v: number) => v > 0);
          const noFrozen = Array.isArray(e.isFrozen) ? e.isFrozen.every((f: boolean) => !f) : true;
          return allPositive && noFrozen && !isTripleTriple;
        }).length;
        const tripleTriples = history.filter((e: any) => {
          const uniqueVals = Array.from(new Set<number>(e.diceValues as number[]));
          return uniqueVals.length === 1 && uniqueVals[0] >= 2 && uniqueVals[0] <= 6;
        }).length;
        const totalTriples = triples + tripleTriples;
        const freezes = history.reduce(
          (n: number, e: any) => n + (Array.isArray(e.isFrozen) ? e.diceValues.filter((_: number, i: number) => e.isFrozen[i]).length : 0),
          0
        );
        const turns = history.length;
        const hits = history.filter((e: any) => e.diceValues.some((v: number) => v > 0)).length;
        stats[player.id] = {
          triplesAvg: totalTriples, // one game → average equals the game's total
          accuracy: turns > 0 ? (hits / turns) * 100 : 0,
          triples: totalTriples,
          freezes,
        };
      });
      const turnLog = buildResultTurnLog(undefined, players);
      return {
        winner: winner.id,
        player1Wins,
        player2Wins,
        points,
        stats,
        ...(turnLog ? { turnLog } : {}),
      };
    }
  };

  /**
   * The Firebase write promise only settles when the server acknowledges. With
   * no connection the SDK queues the write and the promise stays pending
   * forever — the button then sits on "SAVING…" with no way to retry. Bound it
   * so the player always gets back to a RETRY they can press.
   *
   * The queued write is NOT cancelled: if it lands later, the retry's server
   * confirmation simply finds the result already there.
   */
  const SAVE_TIMEOUT_MS = 10000;
  const withTimeout = <T,>(p: Promise<T>, message: string): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(message)), SAVE_TIMEOUT_MS);
      p.then(
        (v) => { clearTimeout(timer); resolve(v); },
        (e) => { clearTimeout(timer); reject(e); }
      );
    });

  const handleSaveTournamentResult = async () => {
    if (!ctx || tournamentSaved) return;
    setTournamentSaving(true);
    const result = computeTournamentResult();
    if (!result) { setTournamentSaving(false); return; }

    // A previous attempt that could not be confirmed on the server may have left
    // an optimistic copy in the SDK's local cache. The completion guards read
    // that same cache, so a plain retry would short-circuit and report success
    // for a result the backend never received — force past it.
    const isRetry = saveAttempted;
    setSaveAttempted(true);

    // Persist the result BEFORE touching the network. Unsent Firebase writes
    // live in memory only, so without this a closed tab loses the result for
    // good. The entry is removed once the server confirms it below; until then
    // the outbox runner keeps retrying it, even after a reload.
    const outboxId = enqueueResult(
      ctx,
      result,
      players.map((p) => p.name).join(' vs '),
      players.map((p) => p.id)
    );

    try {
      const slowMessage =
        'Saving is taking too long — the result is NOT confirmed. Check your connection and press RETRY SAVE.';

      // Without the tournament loaded, completion silently skips BOTH players'
      // history and ELO for a pool match (they are written only when a
      // tournament object is supplied). The playoff branch already refused in
      // this state; the pool branch used to sail through and lose those writes
      // with nothing left to retry them. Fail here instead — the result is in
      // the outbox, and its flush re-reads the tournament fresh.
      if (!fullTournament) {
        throw new Error('Tournament data is still loading — wait a moment and try again.');
      }

      // A throw from completion does NOT mean the result is missing. The result
      // is written first; propagation (advancement, champion) comes after and
      // can fail on its own — declaring a champion is denied outright for a
      // device signed in as someone who is not a participant of this
      // tournament. Confirm against the server either way, or a saved result
      // gets reported as "NOT SAVED" and the outbox can never clear.
      let propagationError: any = null;
      try {
        if (ctx.phase === 'pool') {
          await withTimeout(
            completeTournamentPoolMatch(ctx.tournamentId, ctx.matchId, result, fullTournament, isRetry),
            slowMessage
          );
        } else if (ctx.roundIndex !== undefined && ctx.matchIndex !== undefined) {
          await withTimeout(
            completeTournamentPlayoffMatch(
              ctx.tournamentId,
              ctx.roundIndex,
              ctx.matchIndex,
              result,
              fullTournament,
              isRetry
            ),
            slowMessage
          );
        } else {
          throw new Error('This match is missing its bracket position — tell the organiser.');
        }
      } catch (e: any) {
        propagationError = e;
      }

      // Do NOT clear the save button on the write promise alone. Read the match
      // back FROM THE SERVER and require it to carry this exact result. Spotty
      // venue wifi was showing players "saved" for results that never reached
      // the backend, and wins had to be awarded by hand afterwards.
      const landed = await withTimeout(
        confirmTournamentMatchSaved(
          ctx.tournamentId,
          ctx.phase === 'pool' ? 'pool' : 'playoff',
          ctx.matchId,
          ctx.roundIndex,
          ctx.matchIndex,
          result.winner
        ),
        'Could not reach the server to confirm the result. Check your connection and press RETRY SAVE.'
      );
      if (!landed) {
        throw (
          propagationError ||
          new Error(
            'Could not confirm the result reached the server — stay on this screen, check your connection and press RETRY SAVE.'
          )
        );
      }

      removeResult(outboxId);
      setTournamentSaved(true);
      // The result is safe. If the follow-up work failed, say so plainly rather
      // than claiming the result was lost — the organiser repairs it in one
      // click and the Monitor flags it.
      setTournamentSaveError('');
      setSavedWithWarning(
        propagationError
          ? 'Result saved. The bracket could not be updated from this device — ask the organiser to press "Repair advancement".'
          : ''
      );
    } catch (e: any) {
      console.error('Error saving tournament result', e);
      setTournamentSaveError(
        e?.message || 'Saving failed — check your connection and try again.'
      );
    }
    setTournamentSaving(false);
  };

  const handleReturnToBracket = () => {
    if (!ctx) return;
    clearGameState();
    if (setPlayers) setPlayers([]);
    navigate(`/tournaments/${ctx.tournamentId}`);
  };
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [playersToReorder, setPlayersToReorder] = useState<Player[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchStartTime, setTouchStartTime] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const reorderListRef = useRef<HTMLDivElement>(null);
  const DRAG_THRESHOLD = 12;
  const LONG_PRESS_DURATION = 120;

  // Aggregated tournament stats from main (minimal)
  const calculateTournamentStats = useCallback(() => {
    if (!isMatchBased || !gameState || !Array.isArray(gameState.matchHistory)) return null;

    const totalMatchesPlayed = gameState.matchHistory.length;
    const perPlayer = players.map((player) => {
      const wins = player.matchWins || 0;
      let totalScore = 0;
      let totalThrows = 0;
      let totalTriples = 0;
      let totalTripleTriples = 0;
      let totalFinalThrows = 0;
      let totalFrozenDice = 0;
      let maxTurnScore = 0;

      gameState.matchHistory.forEach((m) => {
        const ps = m.playerStats?.find((p) => p.playerId === player.id);
        if (ps) {
          totalScore += ps.score;
          totalThrows += ps.throws;
          totalTriples += ps.triples;
          totalTripleTriples += ps.tripleTriples;
          totalFinalThrows += ps.finalThrows;
          totalFrozenDice += ps.frozenDiceCount;
          if (ps.maxTurnScore > maxTurnScore) maxTurnScore = ps.maxTurnScore;
        }
      });

      return {
        id: player.id,
        name: player.name,
        wins,
        totalMatchesPlayed,
        totalScore,
        totalThrows,
        totalTriples,
        totalTripleTriples,
        totalFinalThrows,
        totalFrozenDice,
        maxTurnScore,
        avgScore: totalMatchesPlayed > 0 ? totalScore / totalMatchesPlayed : 0,
        avgScorePerTurn: totalThrows > 0 ? totalScore / totalThrows : 0,
        avgTriples: totalMatchesPlayed > 0 ? totalTriples / totalMatchesPlayed : 0,
      };
    });

    const maxWins = Math.max(...perPlayer.map((p) => p.wins));
    const winner = perPlayer.find((p) => p.wins === maxWins);

    return { perPlayer, winner, totalMatchesPlayed };
  }, [isMatchBased, gameState, players]);

  const handleNewGame = () => {
    setPlayersToReorder([...players]);
    setShowReorderModal(true);
  };

  const handleBackToHome = () => {
    // Leaving with a tournament result that never reached the server is how a
    // result actually gets lost — the game state is cleared below and there is
    // nothing left to retry from. Make it a deliberate choice.
    if (ctx && !tournamentSaved) {
      const leave = window.confirm(
        'This result has NOT been confirmed by the server yet.\n\n' +
          'It is saved on this device and will keep retrying automatically — but ' +
          'staying here and pressing RETRY SAVE once you have signal is the sure way.\n\n' +
          'Leave anyway?'
      );
      if (!leave) return;
    }
    // Clear persisted state and in-memory players to avoid carryover between tournaments
    clearGameState();
    if (setPlayers) setPlayers([]);
    navigate('/');
  };

  // Drag-and-drop handles reordering; explicit up/down handlers removed

  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
    setTouchStartX(touch.clientX);
    setTouchStartTime(Date.now());
    setDraggedIndex(index);
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setDraggedIndex(null);
    setTouchStartY(null);
    setTouchStartX(null);
    setTouchStartTime(null);
    setIsDragging(false);
  };

  const handleTouchMoveNative = useCallback((e: TouchEvent) => {
    if (draggedIndex === null || touchStartY === null || touchStartX === null || touchStartTime === null) return;
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const currentX = touch.clientX;
    const diffY = Math.abs(currentY - touchStartY);
    const diffX = Math.abs(currentX - touchStartX);
    const timeDiff = Date.now() - touchStartTime;
    if (!isDragging) {
      const shouldStart = (diffY > DRAG_THRESHOLD && diffY > diffX) || (timeDiff > LONG_PRESS_DURATION && (diffY > 5 || diffX > 5));
      if (!shouldStart) return;
      setIsDragging(true);
      if (navigator.vibrate) navigator.vibrate(30);
      return;
    }
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!elementBelow) return;
    const item = elementBelow.closest('[data-reorder-index]') as HTMLElement | null;
    if (!item) return;
    const targetIndex = parseInt(item.getAttribute('data-reorder-index') || '0');
    if (targetIndex !== draggedIndex && targetIndex >= 0 && targetIndex < playersToReorder.length) {
      setPlayersToReorder(prev => {
        const list = [...prev];
        const dragged = list[draggedIndex];
        list.splice(draggedIndex, 1);
        list.splice(targetIndex, 0, dragged);
        return list;
      });
      setDraggedIndex(targetIndex);
      if (navigator.vibrate) navigator.vibrate(15);
    }
  }, [draggedIndex, touchStartY, touchStartX, touchStartTime, isDragging, playersToReorder.length]);

  useEffect(() => {
    if (!showReorderModal) return;
    const el = reorderListRef.current;
    if (!el) return;
    el.addEventListener('touchmove', handleTouchMoveNative, { passive: false });
    return () => {
      el.removeEventListener('touchmove', handleTouchMoveNative as any);
    };
  }, [showReorderModal, handleTouchMoveNative]);

  const confirmReorder = () => {
    const reorderedPlayers = playersToReorder.map((player, index) => ({
      ...player,
      isCurrent: index === 0,
      score: 0,
      history: [],
      hasWon: false,
      isFrozen: [false, false, false],
      diceValues: [1, 1, 1],
      turnScore: 0,
    }));

    if (setPlayers) {
      setPlayers(reorderedPlayers);
    }
    const playerData = reorderedPlayers.map(p => ({ id: p.id, name: p.name }));
    // Preserve the originally selected series length for rematch
    const matchCount = gameState?.totalMatches || 1;
    startGame(playerData, matchCount);
    setShowReorderModal(false);
  };

  const cancelReorder = () => {
    setShowReorderModal(false);
    setPlayersToReorder([]);
  };

  const togglePlayerHistory = (playerId: string) => {
    setPlayerHistoryStates(prev => ({
      ...prev,
      [playerId]: !prev[playerId]
    }));
  };

  const calculatePlayerStats = (player: Player) => {
    // If this is a tournament (multi-match), aggregate stats across all matches
    const matchesPlayed = gameState?.matchHistory?.length || 0;
    if (isMatchBased && matchesPlayed > 0) {
      let totalScore = 0;
      let totalThrows = 0;
      let totalTriples = 0;
      let totalTripleTriples = 0;
      let totalFinalThrows = 0;
      let totalFrozenDice = 0;
      let highestTurn = 0;
      let totalTurns = 0;
      let totalHits = 0;

      gameState!.matchHistory.forEach((m) => {
        const ps = m.playerStats?.find((p: any) => p.playerId === player.id);
        if (!ps) return;
        totalScore += ps.score || 0;
        totalThrows += ps.throws || 0;
        totalTriples += (ps.triples || 0);
        totalTripleTriples += (ps.tripleTriples || 0);
        totalFinalThrows += ps.finalThrows || 0;
        totalFrozenDice += ps.frozenDiceCount || 0;
        if (ps.maxTurnScore > highestTurn) highestTurn = ps.maxTurnScore;

        if (Array.isArray(ps.history)) {
          totalTurns += ps.history.length;
          totalHits += ps.history.filter((entry: any) => entry.diceValues?.some((v: number) => v > 0)).length;
        }
      });

      const accuracy = totalTurns > 0 ? Math.round((totalHits / totalTurns) * 100) : 0;
      return {
        score: totalScore,
        throws: totalThrows,
        finalThrows: totalFinalThrows,
        // Already aggregated with tripleTriples counted into ps.triples upstream
        triples: totalTriples,
        tripleTriples: totalTripleTriples,
        frozenDice: totalFrozenDice,
        accuracy,
        highestThrow: highestTurn,
        avgTriples: matchesPlayed > 0 ? totalTriples / matchesPlayed : 0,
      };
    }

    // Use the finalized snapshot from the last match if available
    const lastMatch = gameState?.matchHistory?.[gameState.matchHistory.length - 1];
    const snap = lastMatch?.playerStats?.find((ps: any) => ps.playerId === player.id);
    if (snap) {
      return {
        score: snap.score,
        throws: snap.throws,
        finalThrows: snap.finalThrows,
        triples: snap.triples,
        tripleTriples: snap.tripleTriples,
        frozenDice: snap.frozenDiceCount,
        accuracy: Math.round(snap.accuracy),
        highestThrow: snap.maxTurnScore,
      };
    }

    // Fallback to live state if snapshot not found (should be rare)
    const triplesBase = player.history.filter(entry => {
      const allPositive = entry.diceValues.every((v) => v > 0);
      const noFrozen = Array.isArray(entry.isFrozen) ? entry.isFrozen.every((f) => !f) : true;
      const uniq = Array.from(new Set(entry.diceValues));
      const isTripleTriple = uniq.length === 1 && uniq[0] >= 2 && uniq[0] <= 6;
      return allPositive && noFrozen && !isTripleTriple;
    }).length;
    const tripleTriples = player.history.filter(entry => {
      const uniq = Array.from(new Set(entry.diceValues));
      return uniq.length === 1 && uniq[0] >= 2 && uniq[0] <= 6;
    }).length;
    const triples = triplesBase + tripleTriples;
    const frozenDice = player.history.reduce((sum, entry) => sum + entry.isFrozen.filter(Boolean).length, 0);
    const finalThrows = player.history.filter(entry => entry.isFinalThrowAttempt).length;
    const accuracy = player.history.length > 0 ?
      Math.round((player.history.filter(entry => entry.diceValues.some(v => v > 0)).length / player.history.length) * 100) : 0;
    return {
      score: player.score,
      throws: player.history.length,
      finalThrows,
      triples,
      tripleTriples,
      frozenDice,
      accuracy,
      highestThrow: Math.max(...player.history.map(h => h.turnScore), 0)
    };
  };

  const sortedPlayers = [...players].sort((a, b) => {
    if (a.hasWon && !b.hasWon) return -1;
    if (!a.hasWon && b.hasWon) return 1;
    return 0;
  });

  const matchNumber = gameState?.currentMatch || 1;
  const totalMatches = gameState?.totalMatches || 1;

  return (
    <Container>
      <Header>TRIPLE FREEZER</Header>
      
      <MatchHeading>
        {isMatchBased && totalMatches > 1 ? `TOURNAMENT RESULTS` : `MATCH ${matchNumber}/${totalMatches} RESULTS`}
      </MatchHeading>

      {ctx ? (
        <>
        <ButtonRow>
          {!tournamentSaved ? (
            <ActionButton onClick={handleSaveTournamentResult} disabled={tournamentSaving}>
              <FaTrophy size={20} />
              {tournamentSaving ? 'SAVING...' : tournamentSaveError ? 'RETRY SAVE' : 'SAVE RESULT'}
            </ActionButton>
          ) : (
            <ActionButton onClick={handleReturnToBracket}>
              <FaTrophy size={20} />
              BACK TO BRACKET
            </ActionButton>
          )}
          <ActionButton onClick={handleBackToHome}>
            <FaDoorOpen size={20} />
            EXIT
          </ActionButton>
        </ButtonRow>
        {savedWithWarning && tournamentSaved && (
          <div role="status" style={{
            textAlign: 'center', color: '#ffd27a', fontFamily: "'Roboto Condensed', sans-serif",
            fontSize: 14, margin: '2px 16px 10px',
          }}>
            ⚠️ {savedWithWarning}
          </div>
        )}
        {tournamentSaveError && !tournamentSaved && (
          <div role="alert" style={{
            textAlign: 'center', color: '#ff8a8a', fontFamily: "'Roboto Condensed', sans-serif",
            fontSize: 14, margin: '2px 16px 10px',
          }}>
            ⚠️ {tournamentSaveError} The result is NOT saved yet.
          </div>
        )}
        </>
      ) : (
        <ButtonRow>
          <ActionButton onClick={handleNewGame}>
            <FaSyncAlt size={20} />
            REMATCH
          </ActionButton>
          <ActionButton onClick={handleBackToHome}>
            <FaDoorOpen size={20} />
            EXIT
          </ActionButton>
        </ButtonRow>
      )}

      {isMatchBased && totalMatches > 1 && (
        <SummaryCard>
          <SummaryHeader>
            {winnerName ? `${winnerName} WON` : 'SUMMARY'}
          </SummaryHeader>
          <SummaryBody>
            <div>Tournament winner: {winnerName || '—'}</div>
          </SummaryBody>
          <SummaryBody>
            {gameState?.matchHistory?.map((m, idx) => (
              <div key={idx}>
                Match {m.matchNumber || idx + 1}: {m.winner} wins ({m.turnsPlayed || 0} turns)
              </div>
            ))}
          </SummaryBody>
          {(() => {
            const agg = calculateTournamentStats();
            if (!agg) return null;
            return (
              <SummaryBody>
                <SummaryList>
                  {agg.perPlayer.map((p) => (
                    <SummaryItem key={p.id}>
                      {p.name}: Avg.score/turn {(Math.round((p.avgScorePerTurn || 0) * 10) / 10).toFixed(1)}, Max turn score {p.maxTurnScore}
                    </SummaryItem>
                  ))}
                </SummaryList>
              </SummaryBody>
            );
          })()}
        </SummaryCard>
      )}

      {sortedPlayers.map((player) => {
        const stats = calculatePlayerStats(player);
        const showHistory = playerHistoryStates[player.id] || false;
        return (
          <PlayerStatsContainer key={player.id} $isWinner={player.hasWon}>
            <PlayerHeader>
              {player.hasWon ? '🏆 ' : ''}
              {player.name}
              {isMatchBased && totalMatches > 1 ? ` ${player.matchWins || 0} - ${(gameState?.currentMatch || 1) - (player.matchWins || 0)}` : ''}
            </PlayerHeader>
            <StatsContent>
              <StatsRow>
                <StatsColumn>
                  <div>Score: {stats.score}</div>
                  <div>Throws: {stats.throws}</div>
                  <div>Final throws: {stats.finalThrows}</div>
                  <div>Highest throw: {stats.highestThrow}</div>
                </StatsColumn>
                <StatsColumn>
                  <div>Triples: {stats.triples}</div>
                  {isMatchBased && totalMatches > 1 && (
                    <div>Avg. triples / match: {(Math.round(((stats as any).avgTriples || 0) * 10) / 10).toFixed(1)}</div>
                  )}
                  <div>Triple triples: {stats.tripleTriples}</div>
                  <div>Frozen dice: {stats.frozenDice}</div>
                  <div>Accuracy: {stats.accuracy}%</div>
                </StatsColumn>
              </StatsRow>
              <HistoryRow>
                <HistoryLabel>History</HistoryLabel>
                <HistoryToggle $on={showHistory} onClick={() => togglePlayerHistory(player.id)}>
                  <HistoryKnob $on={showHistory} />
                </HistoryToggle>
              </HistoryRow>
              {showHistory && (
                <HistoryContainer>
                  <HistoryScroll>
                    {(() => {
                      const lastMatch = gameState?.matchHistory?.[gameState.matchHistory.length - 1];
                      const snap = lastMatch?.playerStats?.find((ps: any) => ps.playerId === player.id);
                      const history = snap?.history || player.history;
                      if (!history || history.length === 0) return <div>No history available.</div>;
                      return history.map((entry: any, index: number) => {
                        const isTriple = entry.diceValues.every((v: number) => v > 0);
                        const uniqueVals: number[] = Array.from(new Set<number>(entry.diceValues as number[]));
                        const isTripleTriple = uniqueVals.length === 1 && uniqueVals[0] >= 2 && uniqueVals[0] <= 6;
                        const isAllSix = isTripleTriple && uniqueVals[0] === 6;
                        const isMiss = entry.diceValues.every((v: number) => v === 0);
                        const noFrozen = Array.isArray(entry.isFrozen) ? entry.isFrozen.every((f: boolean) => !f) : true;
                        const frozenSix = entry.isFrozen?.some((f: boolean, i: number) => f && entry.diceValues[i] === 6);
                        const nonFrozenPositive = entry.diceValues.filter((v: number, i: number) => !entry.isFrozen?.[i] && v > 0).length;
                        const tags: string[] = [];
                        if (!isAllSix && entry.isFinalThrowAttempt) tags.push('Final');
                        if (isAllSix) tags.push('Insta Win');
                        else if (isTripleTriple) tags.push('Triple Triple');
                        else if (noFrozen && isTriple) tags.push('Triple');
                        if (isMiss) tags.push('Miss');
                        if (frozenSix && nonFrozenPositive === 2) {
                          tags.push('Frozen x2');
                        }
                        return (
                          <HistoryItem key={index}>
                            Turn {index + 1}: [
                            {entry.diceValues.map((v: number, i: number) => (
                              <>
                                <DieValue key={i} $frozen={!!entry.isFrozen?.[i]}>{v}</DieValue>
                                {i < entry.diceValues.length - 1 ? ', ' : ''}
                              </>
                            ))}
                            ] = {entry.turnScore} pts{tags.length ? ` (${tags.join(', ')})` : ''}
                          </HistoryItem>
                        );
                      });
                    })()}
                  </HistoryScroll>
                </HistoryContainer>
              )}
            </StatsContent>
          </PlayerStatsContainer>
        );
      })}

      {showReorderModal && (
        <ReorderModal>
          <ModalContent>
            <ModalHeader>REORDER PLAYERS</ModalHeader>
            <ModalBody ref={reorderListRef}>
              <ModalDescription>Set player order for the next match:</ModalDescription>
              {playersToReorder.map((player, index) => (
                <PlayerReorderItem
                  key={player.id}
                  data-reorder-index={index}
                  draggable
                  onDragStart={() => setDraggedIndex(index)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (draggedIndex !== null && draggedIndex !== index) {
                      const list = [...playersToReorder];
                      const item = list[draggedIndex];
                      list.splice(draggedIndex, 1);
                      list.splice(index, 0, item);
                      setPlayersToReorder(list);
                      setDraggedIndex(index);
                    }
                  }}
                  onDragEnd={() => setDraggedIndex(null)}
                  onTouchStart={(e) => handleTouchStart(e, index)}
                  onTouchEnd={handleTouchEnd}
                >
                  <Grip>
                    <FaGripLines size={32} />
                  </Grip>
                  <span style={{ flex: 1, marginLeft: 8 }}>{player.name}</span>
                </PlayerReorderItem>
              ))}
            </ModalBody>
            <ModalFooter>
              <FooterButton $variant="secondary" onClick={cancelReorder}>
                <FaRotateLeft />
                Cancel
              </FooterButton>
              <FooterButton onClick={confirmReorder}>
                <FaCheck />
                Confirm
              </FooterButton>
            </ModalFooter>
          </ModalContent>
        </ReorderModal>
      )}
    </Container>
  );
};

export default WinnerScreen;